import { Capacitor } from '@capacitor/core';
import {
	createFileRoute,
	FileRoutesByPath,
	ParsedLocation,
	redirect,
} from '@tanstack/react-router';

export const LOCAL_STORAGE_AUTH_KEY = 'graphweaver-auth';

export const resetLocalAuthKey = async () => {
	localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);

	if (Capacitor.isNativePlatform()) {
		const { Preferences } = await import('@capacitor/preferences');
		Preferences.remove({ key: LOCAL_STORAGE_AUTH_KEY });
	}
};

export const setLocalAuthKey = async (token: string) => {
	localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, token);

	// Also copy to Capacitor storage.
	if (Capacitor.isNativePlatform()) {
		const { Preferences } = await import('@capacitor/preferences');
		Preferences.set({
			key: LOCAL_STORAGE_AUTH_KEY,
			value: token,
		});
	}
};

export const isAuthenticated = () => {
	return Boolean(localStorage.getItem(LOCAL_STORAGE_AUTH_KEY));
};

type TFilePath = keyof FileRoutesByPath;

type FileRouteResult<T extends TFilePath> = ReturnType<typeof createFileRoute<T>>;
type FileRouteParams<T extends TFilePath> = Parameters<FileRouteResult<T>>[0];

export const beforeLoadCheckAuth = async ({ location }: { location: ParsedLocation }) => {
	if (!isAuthenticated()) {
		throw redirect({
			to: '/auth/login',
			search: {
				redirect: location.href,
			},
		});
	}
};

export const createAuthenticatedRoute = <T extends TFilePath>(path: T) => {
	const fileRoute = createFileRoute(path);

	return (params: FileRouteParams<T>) => {
		return fileRoute({
			beforeLoad: beforeLoadCheckAuth,
			...params,
		});
	};
};
